import type { ResponseResult } from './common'
import type { ValuationMethodType } from './global'

export enum GeneralNotificationType {
  ASSET_FDV_CHANGE = 'asset_fdv_change',
  ASSET_PRICE_CHANGE = 'asset_price_change',
  ENTITY_ASSET_BALANCE_LIMIT = 'entity_asset_balance_limit',
  ENTITY_ASSET_BALANCE_CHANGE = 'entity_asset_balance_change',
  FUTURES_FUNDING_PNL = 'futures_funding_pnl',
  FUTURES_AVERAGE_FUNDING = 'futures_average_funding',
  FUTURES_LIQUIDATION_PRICE = 'futures_liquidation_price',
  CLAIMABLE_DETECTED = 'claimable_detected',
  PORTFOLIO_ASSET_PRICE_CHANGE = 'portfolio_asset_price_change',
  REMINDER = 'reminder',
  US_INVESTORS_COUNT_LIMIT = 'us_investors_count_limit',
  US_INVESTORS_TOTAL_INVESTMENT_LIMIT = 'us_investors_total_investment_limit',
}

export type GeneralNotificationFactoryType = 'fe' | 'email' | 'telegram'
export interface GeneralNotificationFactory {
  id?: string
  factory_type: GeneralNotificationFactoryType
  factory_id?: string // only for telegram
}

export interface PortfolioAssetPriceChangeData {
  minimal_share_percent: number
  minimal_change_percent: number
  time_window_days: number
  target_currency_id?: string
  valuation_method?: ValuationMethodType
  tagged_users?: string[]
  note?: string | null
}

export interface AssetNotificationData {
  currency_id?: string
  ico_id?: string
  base_value: number
  target_value: number
  value_currency_id?: string | null
  note: string
  tagged_users?: string[]
}

export interface AssetBalanceChangeNotificationData {
  currency_id?: string
  ico_id?: string
  target_balance_change: number
  note: string
  tagged_users?: string[]
  related_account_id?: string | null
}

export interface ReminderNotificationData {
  note: string
  notify_at: string
  currency_id?: string
  ico_id?: string
  tagged_users?: string[]
}

export interface FuturesPositionIdentity {
  exchange_id: string
  source_id: string | null
  market_symbol: string
  position_side: string
  side: 'long' | 'short'
  position_open_time: string
}

export interface FuturesLiquidationNotificationData extends FuturesPositionIdentity {
  trigger_price: number
  note?: string | null
  tagged_users?: string[]
}

export interface ClaimableDetectedNotificationData {
  worth_threshold_usd: number
  note?: string | null
  tagged_users?: string[]
}

export interface GeneralNotificationParamsBase {
  root_id?: string | null
  entity_id?: string | null
  is_recurring: boolean
  notification_factories?: GeneralNotificationFactory[]
}

export interface PortfolioAssetPriceChangeParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.PORTFOLIO_ASSET_PRICE_CHANGE
  data: PortfolioAssetPriceChangeData
}

export type AssetNotificationType =
  | GeneralNotificationType.ASSET_PRICE_CHANGE
  | GeneralNotificationType.ASSET_FDV_CHANGE
  | GeneralNotificationType.ENTITY_ASSET_BALANCE_LIMIT

export interface AssetNotificationParams extends GeneralNotificationParamsBase {
  type: AssetNotificationType
  data: AssetNotificationData
}

export interface AssetBalanceChangeNotificationParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.ENTITY_ASSET_BALANCE_CHANGE
  data: AssetBalanceChangeNotificationData
}

export interface ReminderNotificationParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.REMINDER
  data: ReminderNotificationData
}

export interface FuturesLiquidationNotificationParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.FUTURES_LIQUIDATION_PRICE
  entity_id: string
  data: FuturesLiquidationNotificationData
}

export type FuturesLiquidationNotification =
  FuturesLiquidationNotificationParams & {
    id: string
    created: string
  }

export type FuturesPositionNotification =
  | FuturesLiquidationNotification
  | FuturesAverageFundingNotification
  | FuturesFundingPnlNotification

export interface FuturesAverageFundingNotificationData extends Omit<
  FuturesLiquidationNotificationData,
  'trigger_price'
> {
  trigger_rate: number
  comparator: '>=' | '<='
}

export interface FuturesAverageFundingNotificationParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.FUTURES_AVERAGE_FUNDING
  entity_id: string
  data: FuturesAverageFundingNotificationData
}

export type FuturesAverageFundingNotification =
  FuturesAverageFundingNotificationParams & {
    id: string
    created: string
  }

export interface FuturesFundingPnlNotificationData extends Omit<
  FuturesAverageFundingNotificationData,
  'trigger_rate'
> {
  trigger_amount: number
  currency: string
}
export interface FuturesFundingPnlNotificationParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.FUTURES_FUNDING_PNL
  entity_id: string
  data: FuturesFundingPnlNotificationData
}
export type FuturesFundingPnlNotification =
  FuturesFundingPnlNotificationParams & { id: string; created: string }
export interface ClaimableDetectedNotificationParams extends GeneralNotificationParamsBase {
  type: GeneralNotificationType.CLAIMABLE_DETECTED
  entity_id: string
  data: ClaimableDetectedNotificationData
}

export type ClaimableDetectedNotification =
  ClaimableDetectedNotificationParams & {
    id: string
    created: string
  }

export interface USInvestorsNotificationCountLimitData {
  max_investors_count: number
  tagged_users?: string[]
  note?: string | null
}

export interface USInvestorsNotificationTotalInvestmentLimitData {
  max_total_investment: number
  tagged_users?: string[]
  note?: string | null
}

export type USInvestorsNotificationType =
  | GeneralNotificationType.US_INVESTORS_COUNT_LIMIT
  | GeneralNotificationType.US_INVESTORS_TOTAL_INVESTMENT_LIMIT

export interface USInvestorsNotificationParams extends GeneralNotificationParamsBase {
  type: USInvestorsNotificationType
  data:
    | USInvestorsNotificationCountLimitData
    | USInvestorsNotificationTotalInvestmentLimitData
}

export type USInvestorsNotification = USInvestorsNotificationParams & {
  id: string
  created: string
}

export type GeneralNotificationParams =
  | PortfolioAssetPriceChangeParams
  | AssetNotificationParams
  | AssetBalanceChangeNotificationParams
  | ReminderNotificationParams
  | FuturesLiquidationNotificationParams
  | FuturesAverageFundingNotificationParams
  | FuturesFundingPnlNotificationParams
  | ClaimableDetectedNotificationParams
  | USInvestorsNotificationParams

export type AssetNotification = AssetNotificationParams & {
  id: string
  created: string
}

export type PortfolioAssetPriceChangeNotification =
  PortfolioAssetPriceChangeParams & {
    id: string
    created: string
  }

export type AssetBalanceChangeNotification =
  AssetBalanceChangeNotificationParams & {
    id: string
    created: string
  }

export type ReminderNotification = ReminderNotificationParams & {
  id: string
  created: string
}

export type GeneralNotification = GeneralNotificationParams & {
  id: string
  created: string
}

export interface GeneralNotificationResponse {
  data: GeneralNotification
  result: ResponseResult
}

export interface GeneralNotificationsResponse {
  data: GeneralNotification[]
  result: ResponseResult
}

export interface TaggedUser {
  id: string
  root_id: string
  tag: string
  created: string
}

export interface TaggedUserResponse {
  data: TaggedUser
  result: ResponseResult
}

export interface TaggedUsersResponse {
  data: TaggedUser[]
  result: ResponseResult
}
